import { SearchResult, ClientData, ClientAddress, Provenance } from './types'

/**
 * Transforms a search result from the API into a standardized ClientData object
 * Handles both naming conventions for first/last names (firstname/first_name, lastname/last_name)
 */
export const transformSearchResultToClientData = (searchResult: SearchResult): ClientData => {
    const client = searchResult.client
    return {
        client_id: client.client_id || client.id || 0,
        origin: searchResult.origin,
        provenance: searchResult.provenance,
        firstname: client.firstname,
        lastname: client.lastname,
        dob: client.dob,
        email: client.email,
        telephone: client.telephone,
        cfa_signed: client.cfa_signed,
        cfa_signed_date: client.cfa_signed_date,
        esign_link: client.esign_link,
        base_cfa_link: client.base_cfa_link,
        has_blocked_claims: false, // Default value, adjust if needed
        blocked_claims_info: null, // Default value, adjust if needed
        current_address: client.current_address || {
            house_number: client.house_number,
            house_name: client.house_name,
            flat: client.flat,
            street: client.street,
            town_city: client.town_city,
            city: client.city,
            county: client.county,
            postcode: client.postcode,
            raw_address: client.raw_address,
        },
        previous_addresses: [], // Default empty array
        claims: searchResult.claims,
    }
}

/**
 * Formats a date string to UK locale format
 */
export const formatDate = (dateString: string): string => {
    if (!dateString) return 'Not provided'
    const date = new Date(dateString)
    // Legacy stores (OA, Zapped) hold free-text DOBs that don't parse.
    if (Number.isNaN(date.getTime())) return dateString
    return date.toLocaleDateString('en-GB')
}

/** Formats a timestamp as `dd/mm/yyyy, hh:mm:ss` in UK locale; empty input → '—'. */
export const formatDateTime = (value?: string | null): string => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
}

/** Pill tone for a provenance channel: legacy stores warn, API/master are informational, the rest neutral-accent. */
export const provenanceTone = (provenance?: Provenance | null): 'accent' | 'info' | 'warn' | 'neutral' | 'good' => {
    if (!provenance) return 'neutral'
    if (provenance.legacy) return 'warn'
    switch (provenance.channel) {
        case 'api':
            return 'info'
        case 'manual':
        case 'master':
            return 'neutral'
        case 'website':
            return 'good'
        default:
            return 'accent'
    }
}

/** 1536 → "1.5 KB". Null/unknown sizes → '—'. */
export const formatBytes = (bytes?: number | null): string => {
    if (bytes == null || Number.isNaN(bytes)) return '—'
    if (bytes < 1024) return `${bytes} B`
    const units = ['KB', 'MB', 'GB']
    let value = bytes / 1024
    let unit = 0
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024
        unit += 1
    }
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`
}

/**
 * Parses a `YYYY-MM-DD` (or ISO timestamp) string into a local Date, ignoring
 * the time component so the calendar day is never shifted by timezone.
 */
export const parseIsoDate = (value?: string | null): Date | null => {
    if (!value) return null
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
    if (!match) return null
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    return Number.isNaN(date.getTime()) ? null : date
}

/** Formats a Date as `YYYY-MM-DD` using local calendar fields. */
export const toIsoDate = (date: Date | null | undefined): string | undefined => {
    if (!date) return undefined
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Gets the first name from a ClientData object, handling both naming conventions
 */
export const getFirstName = (client: ClientData): string => {
    return client.firstname || 'Not provided'
}

/**
 * Gets the last name from a ClientData object, handling both naming conventions
 */
export const getLastName = (client: ClientData): string => {
    return client.lastname || 'Not provided'
}

/**
 * Gets the full name from a ClientData object
 */
export const getFullName = (client: ClientData): string => {
    const firstName = getFirstName(client)
    const lastName = getLastName(client)
    // Collapse stray whitespace from source records ("John " + "Smith").
    return `${firstName} ${lastName}`.replace(/\s+/g, ' ').trim()
}

/**
 * Formats an address object into a readable string
 */
export const getFormattedAddress = (address: ClientAddress): string => {
    const parts = []

    if (address.flat) parts.push(address.flat)
    if (address.house_number) {
        const housePart = address.house_name
            ? `${address.house_name}, ${address.house_number} ${address.street}`
            : `${address.house_number} ${address.street}`
        parts.push(housePart)
    }
    if (address.town_city) parts.push(address.town_city)
    if (address.county) parts.push(address.county)
    if (address.postcode) parts.push(address.postcode)

    return parts.join(', ')
}

/**
 * Validates if a string is a valid email address
 */
export const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email)
}

/**
 * Validates if a string is a valid UK phone number with +44 country code
 */
export const isValidUKPhone = (phone: string): boolean => {
    // Remove any spaces or dashes for validation
    const cleanPhone = phone.replace(/[\s-]/g, '')

    // UK phone number patterns:
    // +44 followed by 10 digits (mobile or landline)
    // +44 followed by area code (2-5 digits) and local number (6-8 digits)
    const ukPhoneRegex = /^\+44\d{10}$/

    return ukPhoneRegex.test(cleanPhone)
}
